import React, { useEffect, useState } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import moment from 'moment'
import { WidgetFrame, WidgetUnavailable, BigNumber } from './WidgetFrame'

// ---- Static / client-side widgets -------------------------------------------

export const MessageWidget = ({ widget }) => {
  const { viz } = useTheme().palette
  return (
    <WidgetFrame title={widget.config?.title || 'Notice'} accent={viz.series}>
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography
          sx={{
            fontSize: 'clamp(1.2rem, 2.2vw, 2rem)',
            fontWeight: 500,
            whiteSpace: 'pre-wrap',
            textAlign: 'center',
            maxWidth: '90%',
          }}
        >
          {widget.config?.text || 'Add a message in the wallboard editor'}
        </Typography>
      </Box>
    </WidgetFrame>
  )
}

export const ClockWidget = () => {
  const [now, setNow] = useState(new Date())
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000)
    return () => clearInterval(timer)
  }, [])
  return (
    <WidgetFrame>
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: 'clamp(2.4rem, 5vw, 4.4rem)',
            lineHeight: 1.05,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {moment(now).format('HH:mm')}
        </Typography>
        <Typography sx={{ color: 'text.secondary', fontSize: '1.05rem' }}>
          {moment(now).format('dddd D MMMM')}
        </Typography>
      </Box>
    </WidgetFrame>
  )
}

// ---- Count widgets -----------------------------------------------------------

export const ShiftsTodayWidget = ({ data }) => {
  const { viz } = useTheme().palette
  const counts = data?.shiftCounts
  return (
    <WidgetFrame title="Shifts today" accent={viz.series}>
      {!counts || counts.error ? (
        <WidgetUnavailable />
      ) : (
        <BigNumber
          value={counts.shiftsToday}
          label={`${counts.onDutyNow} on duty right now`}
        />
      )}
    </WidgetFrame>
  )
}

export const UncoveredShiftsWidget = ({ data }) => {
  const { viz } = useTheme().palette
  const counts = data?.shiftCounts
  const uncovered = counts?.uncoveredNext7 ?? 0
  const accent = uncovered > 0 ? viz.warning : viz.good
  return (
    <WidgetFrame title="Uncovered shifts" accent={accent}>
      {!counts || counts.error ? (
        <WidgetUnavailable />
      ) : (
        <BigNumber
          value={uncovered}
          label={`of ${counts.shiftsNext7} shifts in the next 7 days`}
          color={uncovered > 0 ? viz.warning : viz.good}
        />
      )}
    </WidgetFrame>
  )
}

export const EpcrStatsWidget = ({ data }) => {
  const { viz } = useTheme().palette
  const stats = data?.epcrStats
  return (
    <WidgetFrame title="Patient records" accent={viz.series}>
      {!stats || stats.error ? (
        <WidgetUnavailable />
      ) : (
        <BigNumber value={stats.last30Days} label="ePCRs in the last 30 days" />
      )}
    </WidgetFrame>
  )
}

const FleetFigure = ({ value, label, color }) => (
  <Box sx={{ textAlign: 'center' }}>
    <Typography
      sx={{
        fontWeight: 700,
        fontSize: 'clamp(1.8rem, 3.4vw, 3rem)',
        lineHeight: 1.1,
        color,
      }}
    >
      {value ?? '—'}
    </Typography>
    <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
      {label}
    </Typography>
  </Box>
)

export const FleetReadinessWidget = ({ data }) => {
  const { viz } = useTheme().palette
  const fleet = data?.fleetReadiness
  return (
    <WidgetFrame title="Fleet readiness" accent={viz.series}>
      {!fleet || fleet.error ? (
        <WidgetUnavailable />
      ) : (
        <Stack
          direction="row"
          sx={{ flex: 1, alignItems: 'center', justifyContent: 'space-around' }}
        >
          <FleetFigure
            value={fleet.operationalVehicles}
            label="Operational"
            color={viz.good}
          />
          <FleetFigure
            value={fleet.offRoadVehicles}
            label="Off road"
            color={fleet.offRoadVehicles > 0 ? viz.warning : 'text.primary'}
          />
          <FleetFigure
            value={fleet.openDefects}
            label="Open defects"
            color={fleet.openDefects > 0 ? viz.critical : 'text.primary'}
          />
        </Stack>
      )}
    </WidgetFrame>
  )
}

export const PtsTodayWidget = ({ data }) => {
  const { viz } = useTheme().palette
  const pts = data?.ptsToday
  return (
    <WidgetFrame title="PTS today" accent={viz.series}>
      {!pts || pts.error ? (
        <WidgetUnavailable />
      ) : (
        <Stack
          direction="row"
          sx={{ flex: 1, alignItems: 'center', justifyContent: 'space-around' }}
        >
          <FleetFigure value={pts.booked} label="Booked" />
          <FleetFigure value={pts.completed} label="Completed" color={viz.good} />
          <FleetFigure value={pts.inProgress} label="In progress" />
        </Stack>
      )}
    </WidgetFrame>
  )
}

export const CqcReadinessWidget = ({ data }) => {
  const { viz } = useTheme().palette
  const cqc = data?.cqcReadiness
  const score = cqc?.overallScore
  const color =
    score == null
      ? 'text.primary'
      : score >= 80
        ? viz.good
        : score >= 60
          ? viz.warning
          : viz.critical
  return (
    <WidgetFrame title="CQC readiness" accent={viz.series}>
      {!cqc || cqc.error ? (
        <WidgetUnavailable />
      ) : (
        <BigNumber
          value={score == null ? null : Math.round(score)}
          label={score == null ? 'Not yet assessed' : 'overall readiness score'}
          color={color}
        />
      )}
    </WidgetFrame>
  )
}
